import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  ContactoChatDto,
  MensajesPagedBackend,
  ChatMensajeBackend,
  ChatMensaje,
} from '../models/chat.model';

/**
 * Cliente HTTP de `/api/chat/*`. Cubre la parte REST del módulo (contactos,
 * histórico paginado, marcar leído, editar/eliminar). El envío de mensajes
 * en vivo va por `ChatHubService` (SignalR).
 *
 * El `JwtInterceptor` global agrega el Bearer + el `AcceptLanguageInterceptor`
 * agrega el header `Accept-Language` — los errores de validación llegan en
 * el idioma activo.
 */
@Injectable({ providedIn: 'root' })
export class ChatApiService {
  private http = inject(HttpClient);
  private readonly base = '/api/chat';

  contactos(): Observable<ContactoChatDto[]> {
    return this.http.get<ContactoChatDto[]>(`${this.base}/contactos`);
  }

  /**
   * Trae un slice del histórico. Sin `before` → primera página (mensajes
   * más recientes). Con `before` → mensajes con `id < before` (cursor descendente).
   */
  mensajes(
    otroUsuarioId: number,
    opts: { before?: number; limit?: number } = {},
  ): Observable<MensajesPagedBackend> {
    let params = new HttpParams();
    if (opts.before !== undefined) params = params.set('before', String(opts.before));
    if (opts.limit !== undefined) params = params.set('limit', String(opts.limit));
    return this.http.get<MensajesPagedBackend>(
      `${this.base}/conversaciones/${otroUsuarioId}/mensajes`,
      { params },
    );
  }

  /** Idempotente (204 NoContent). */
  marcarLeido(otroUsuarioId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/conversaciones/${otroUsuarioId}/leido`, null);
  }

  /** Edita el texto de un mensaje propio. 403 si no soy el remitente, 409 si está eliminado. */
  editar(mensajeId: number, texto: string): Observable<ChatMensaje> {
    return this.http
      .put<ChatMensajeBackend>(`${this.base}/mensajes/${mensajeId}`, { texto })
      .pipe(map(parseMensaje));
  }

  /** Soft delete (204). El front debe mostrar "Mensaje eliminado" en su lugar. */
  eliminar(mensajeId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/mensajes/${mensajeId}`);
  }

  /**
   * Sube un adjunto (imagen JPG/PNG/GIF/WebP o PDF, máx 10 MB) y crea un mensaje
   * con texto opcional. El backend valida tamaño + MIME y rechaza con 400 si
   * algo no cumple. 403 si la conv no es elegible.
   */
  enviarAdjunto(otroUsuarioId: number, archivo: File, texto?: string): Observable<ChatMensaje> {
    const form = new FormData();
    form.append('archivo', archivo, archivo.name);
    if (texto && texto.trim()) form.append('texto', texto.trim());
    return this.http
      .post<ChatMensajeBackend>(`${this.base}/conversaciones/${otroUsuarioId}/adjunto`, form)
      .pipe(map(parseMensaje));
  }
}

/** Parsea el JSON crudo del API a un `ChatMensaje` con `Date`. Export aux también
 *  consumido por `ChatHubService` al recibir eventos. */
export function parseMensaje(m: ChatMensajeBackend): ChatMensaje {
  return {
    id: m.id,
    conversacionId: m.conversacionId,
    remitenteId: m.remitenteId,
    remitenteNombre: m.remitenteNombre,
    texto: m.texto,
    enviadoEn: new Date(m.enviadoEn),
    leidoEn: m.leidoEn ? new Date(m.leidoEn) : null,
    editado: m.editado,
    eliminado: m.eliminado,
    adjunto: m.adjunto
      ? {
          id: m.adjunto.id,
          nombreOriginal: m.adjunto.nombreOriginal,
          // Si el server NO mandó mimeType, lo inferimos desde la extensión
          // para que el render decida bien entre `<img>` inline o chip-de-archivo.
          mimeType: m.adjunto.mimeType || inferirMimeTypeDesdeNombre(m.adjunto.nombreOriginal),
          tamanoBytes: m.adjunto.tamanoBytes,
          url: m.adjunto.url,
        }
      : null,
  };
}

/** Mapeo extensión → MIME para el fallback cuando el backend manda mimeType vacío. */
function inferirMimeTypeDesdeNombre(nombre: string | undefined | null): string {
  if (!nombre) return '';
  const ext = nombre.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png':  return 'image/png';
    case 'gif':  return 'image/gif';
    case 'webp': return 'image/webp';
    case 'bmp':  return 'image/bmp';
    case 'svg':  return 'image/svg+xml';
    case 'pdf':  return 'application/pdf';
    default:     return '';
  }
}
